import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, X, ArrowLeft } from "lucide-react";
import { fetchTheme, fetchContributions } from "@/lib/api";
import ContributionCard from "@/components/ContributionCard";
import { THEME } from "@/constants/testIds";
import { useT } from "@/lib/i18n";

export default function ThemePage() {
    const { slug } = useParams();
    const { t } = useT();
    const [theme, setTheme] = useState(null);
    const [contributions, setContributions] = useState([]);
    const [loading, setLoading] = useState(true);

    const [q, setQ] = useState("");
    const [school, setSchool] = useState("");
    const [country, setCountry] = useState("");

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const [t, c] = await Promise.all([
                    fetchTheme(slug),
                    fetchContributions({ theme: slug }),
                ]);
                if (cancelled) return;
                setTheme(t);
                setContributions(c);
            } catch (e) {
                /* noop */
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        window.scrollTo({ top: 0, behavior: "instant" });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    const schools = useMemo(
        () => Array.from(new Set(contributions.map((c) => c.school))).sort(),
        [contributions]
    );
    const countries = useMemo(
        () => Array.from(new Set(contributions.map((c) => c.country))).sort(),
        [contributions]
    );

    const filtered = useMemo(() => {
        const ql = q.trim().toLowerCase();
        return contributions.filter((c) => {
            if (school && c.school !== school) return false;
            if (country && c.country !== country) return false;
            if (ql) {
                const hay = `${c.student_name} ${c.school} ${c.text} ${c.excerpt}`.toLowerCase();
                if (!hay.includes(ql)) return false;
            }
            return true;
        }).map((c) => ({ ...c, accent: theme?.accent }));
    }, [contributions, q, school, country, theme]);

    const clearAll = () => {
        setQ("");
        setSchool("");
        setCountry("");
    };

    if (!theme && !loading) {
        return (
            <div className="container-pad py-32 text-center">
                <p className="text-ink-muted">{t("common.themeNotFound")}</p>
                <Link to="/" className="btn-secondary mt-6">{t("common.backHome")}</Link>
            </div>
        );
    }

    return (
        <div data-testid={THEME.page(slug)}>
            {/* HERO */}
            <section className="relative overflow-hidden">
                {theme?.image_url && (
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: `url(${theme.image_url})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }}
                    />
                )}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(160deg, rgba(31,69,48,0.78) 0%, rgba(15,46,69,0.82) 100%)",
                    }}
                />
                <div className="container-pad relative py-20 md:py-32 text-white">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white mb-8"
                    >
                        <ArrowLeft size={14} strokeWidth={1.6} />
                        {t("common.allThemes")}
                    </Link>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="caption text-white/80 mb-4">{theme?.subtitle}</div>
                        <h1 className="heading-serif text-5xl md:text-7xl leading-[0.95] tracking-tight text-balance">
                            {theme?.title}
                        </h1>
                        <p className="mt-6 text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed">
                            {theme?.tagline}
                        </p>
                        <p className="mt-6 text-white/75 max-w-3xl leading-relaxed">
                            {theme?.description}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* FILTERS */}
            <section className="sticky top-16 md:top-20 z-30 bg-bg-base/85 backdrop-blur-xl border-b border-black/5">
                <div className="container-pad py-4 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted"
                            size={16}
                            strokeWidth={1.6}
                        />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={t("common.search")}
                            data-testid={THEME.searchInput}
                            className="w-full pl-11 pr-4 py-3 bg-white border border-black/10 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green/40"
                        />
                    </div>
                    <select
                        value={school}
                        onChange={(e) => setSchool(e.target.value)}
                        data-testid={THEME.schoolFilter}
                        className="px-4 py-3 bg-white border border-black/10 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-green/30 md:min-w-[220px]"
                    >
                        <option value="">{t("common.allSchools")}</option>
                        {schools.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    <select
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        data-testid={THEME.countryFilter}
                        className="px-4 py-3 bg-white border border-black/10 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-green/30 md:min-w-[160px]"
                    >
                        <option value="">{t("common.allCountries")}</option>
                        {countries.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                    {(q || school || country) && (
                        <button
                            type="button"
                            onClick={clearAll}
                            data-testid={THEME.clearFilters}
                            className="inline-flex items-center gap-1 text-xs text-ink-secondary hover:text-brand-green px-3 py-2"
                        >
                            <X size={14} strokeWidth={1.6} /> {t("common.clear")}
                        </button>
                    )}
                </div>
            </section>

            {/* GRID */}
            <section className="container-pad py-16 md:py-20">
                <div className="flex items-baseline justify-between mb-10">
                    <h2 className="heading-serif text-2xl md:text-3xl text-ink-primary">
                        {filtered.length} contribution{filtered.length === 1 ? "" : "s"}
                    </h2>
                    <span className="caption text-ink-muted hidden sm:block">
                        / {theme?.contribution_count} total
                    </span>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className="rounded-2xl bg-white border border-black/5 h-64 animate-pulse"
                            />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div
                        data-testid={THEME.emptyState}
                        className="text-center py-20 text-ink-muted"
                    >
                        <p className="heading-serif text-2xl mb-2">No matches yet.</p>
                        <p className="text-sm">Try clearing filters or searching with different keywords.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filtered.map((c, i) => (
                            <ContributionCard key={c.id} contribution={c} index={i} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
